import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  output,
  TemplateRef,
  ViewContainerRef,
} from "@angular/core";
import { Overlay, OverlayRef } from "@angular/cdk/overlay";
import { TemplatePortal } from "@angular/cdk/portal";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { PopoverService } from "./popover.service";

@Directive({
  selector: "[popoverTrigger]",
  exportAs: "popoverElement",
})
export class PopoverDirective implements OnInit, OnDestroy {
  @Input()
  popoverTrigger!: TemplateRef<object>;

  @Input()
  popoverContext: any;

  @Input()
  popoverDisabled: boolean = false;

  @Input()
  closeOnClickOutside: boolean = false;

  @Input()
  popoverAnimation: "zoom" | "none" = "none";

  popoverEnd = output();

  private unsubscribe = new Subject<void>();
  // PERF: overlay créé lazily au premier clic au lieu de ngOnInit
  private overlayRef: OverlayRef | null = null;

  constructor(
    private elementRef: ElementRef,
    private overlay: Overlay,
    private vcr: ViewContainerRef,
    private popoverService: PopoverService,
  ) {}

  ngOnInit(): void {
    // PERF: overlay n'est plus créé ici, mais au premier clic
    this.popoverService
      .getState()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe((resp) => {
        if (resp) {
          this.detachOverlay();
        }
      });
  }

  ngOnDestroy(): void {
    this.detachOverlay();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  @HostListener("click")
  clickou() {
    if (!this.popoverDisabled) {
      // PERF: création lazy de l'overlay au premier clic
      if (!this.overlayRef) {
        this.createOverlay();
      }
      this.attachOverlay();
    }
  }

  private createOverlay(): void {
    const scrollStrategy = this.overlay.scrollStrategies.reposition();
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.elementRef)
      .withPositions([
        // Bas (préféré)
        {
          originX: "start",
          originY: "bottom",
          overlayX: "start",
          overlayY: "top",
        },
        // Bas centré
        {
          originX: "center",
          originY: "bottom",
          overlayX: "center",
          overlayY: "top",
        },
        // Haut
        {
          originX: "start",
          originY: "top",
          overlayX: "start",
          overlayY: "bottom",
        },
        // Haut centré
        {
          originX: "center",
          originY: "top",
          overlayX: "center",
          overlayY: "bottom",
        },
        // Droite
        {
          originX: "end",
          originY: "center",
          overlayX: "start",
          overlayY: "center",
        },
        // Gauche
        {
          originX: "start",
          originY: "center",
          overlayX: "end",
          overlayY: "center",
        },
      ])
      .withViewportMargin(8)
      .withPush(true);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy,
      hasBackdrop: true,
      backdropClass: "",
    });

    this.overlayRef
      .backdropClick()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(() => {
        if (this.closeOnClickOutside) {
          this.detachOverlay();
        }
      });
  }

  public attachOverlay(params?: Object): void {
    if (!this.overlayRef) {
      this.createOverlay();
    }
    if (!this.overlayRef.hasAttached()) {
      const portal = new TemplatePortal(this.popoverTrigger, this.vcr, {
        ...this.popoverContext,
        ...params,
        close: () => this.detachOverlay(),
      });

      this.overlayRef.attach(portal);

      if (this.popoverAnimation === "zoom") {
        this.playZoomIn();
      }
    }
  }

  public detachOverlay(): void {
    this.popoverEnd.emit();
    if (!this.overlayRef?.hasAttached()) return;

    if (this.popoverAnimation === "zoom") {
      this.playZoomOut();
    } else {
      this.overlayRef.detach();
    }
  }

  // ===================== ANIMS =====================

  private playZoomIn() {
    if (!this.overlayRef) return;
    const overlayEl = this.overlayRef.overlayElement as HTMLElement;
    const triggerRect =
      this.elementRef.nativeElement.getBoundingClientRect() as DOMRect;

    overlayEl.style.visibility = "hidden";

    requestAnimationFrame(() => {
      const overlayRect = overlayEl.getBoundingClientRect() as DOMRect;

      const triggerCx = triggerRect.left + triggerRect.width / 2;
      const triggerCy = triggerRect.top + triggerRect.height / 2;
      const overlayCx = overlayRect.left + overlayRect.width / 2;
      const overlayCy = overlayRect.top + overlayRect.height / 2;

      const dx = triggerCx - overlayCx;
      const dy = triggerCy - overlayCy;

      const scaleX = triggerRect.width / overlayRect.width;
      const scaleY = triggerRect.height / overlayRect.height;

      overlayEl.style.transformOrigin = "center center";
      overlayEl.style.transition = "none";
      overlayEl.style.transform = `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`;
      overlayEl.style.opacity = "0";

      requestAnimationFrame(() => {
        overlayEl.style.visibility = "visible";
        overlayEl.style.transition = "transform 300ms ease, opacity 300ms ease";
        overlayEl.style.transform = "translate(0, 0) scale(1, 1)";
        overlayEl.style.opacity = "1";
      });
    });
  }

  private playZoomOut() {
    if (!this.overlayRef) return;
    const overlayEl = this.overlayRef.overlayElement as HTMLElement;
    const triggerRect =
      this.elementRef.nativeElement.getBoundingClientRect() as DOMRect;
    const overlayRect = overlayEl.getBoundingClientRect() as DOMRect;

    const triggerCx = triggerRect.left + triggerRect.width / 2;
    const triggerCy = triggerRect.top + triggerRect.height / 2;
    const overlayCx = overlayRect.left + overlayRect.width / 2;
    const overlayCy = overlayRect.top + overlayRect.height / 2;

    const dx = triggerCx - overlayCx;
    const dy = triggerCy - overlayCy;
    const scaleX = triggerRect.width / overlayRect.width;
    const scaleY = triggerRect.height / overlayRect.height;

    overlayEl.style.transformOrigin = "center center";
    overlayEl.style.transition = "transform 200ms ease, opacity 200ms ease";
    overlayEl.style.transform = `translate(${dx}px, ${dy}px) scale(${scaleX}, ${scaleY})`;
    overlayEl.style.opacity = "0";

    const onEnd = () => {
      overlayEl.removeEventListener("transitionend", onEnd);
      this.overlayRef?.detach();
      overlayEl.style.transition = "";
      overlayEl.style.transform = "";
      overlayEl.style.opacity = "";
      overlayEl.style.visibility = "";
    };
    overlayEl.addEventListener("transitionend", onEnd);
  }
}
